import { Link } from 'react-router-dom';
import { MapPin, Calendar, Users } from 'lucide-react';

interface ClassCardProps {
  id: string;
  title: string;
  providerName: string;
  thumbnailUrl: string;
  price: number;
  discountPrice: number;
  startDate: string;
  endDate: string;
  location: string;
  locationType: 'ONLINE' | 'OFFLINE' | 'HYBRID';
  classType: 'CERTIFICATE' | 'COMPLETION' | 'WORKSHOP' | 'OTHER';
  status: 'SCHEDULED' | 'RECRUITING' | 'CLOSED' | 'IN_PROGRESS' | 'COMPLETED';
  capacity: number;
  currentCount: number;
  closingSoon?: boolean;
  isPremium?: boolean;
  rank?: number;
  categoryPath?: { id: string; name: string; level: number }[];
  instructors?: {
    id: string;
    name: string;
    description: string;
    image_url: string | null;
  }[];
  onInstructorClick?: (instructorId: string) => void;
}

// 카테고리 레벨(1·2·3차)별 옅은 파스텔 태그 색상
const categoryTagColors: Record<number, string> = {
  1: 'bg-indigo-50 text-indigo-600',
  2: 'bg-emerald-50 text-emerald-600',
  3: 'bg-amber-50 text-amber-600',
};

// 인기 순위(1~8) 숫자 색상 — 순위마다 다른 색
const RANK_COLORS = [
  '#EF4444', // 1 red
  '#F97316', // 2 orange
  '#F59E0B', // 3 amber
  '#22C55E', // 4 green
  '#06B6D4', // 5 cyan
  '#3B82F6', // 6 blue
  '#8B5CF6', // 7 violet
  '#EC4899', // 8 pink
];

// 카드 딱지는 모집중/모집마감 두 가지로만 단순화 (모집예정·진행중·종료를 세분화하면 헷갈림)
const statusLabels = {
  RECRUITING: { text: '모집중', color: 'bg-accent text-white' },
  NOT_RECRUITING: { text: '모집 마감', color: 'bg-gray-500 text-white' },
};

const locationTypeLabels = {
  ONLINE: '온라인',
  OFFLINE: '오프라인',
  HYBRID: '온/오프라인',
};

const classTypeLabels = {
  CERTIFICATE: { text: '자격증', color: 'bg-accent text-white' },
  COMPLETION: { text: '수료증', color: 'bg-blue-500 text-white' },
  WORKSHOP: { text: '워크샵', color: 'bg-purple-500 text-white' },
  OTHER: { text: '기타', color: 'bg-gray-400 text-white' },
};

export default function ClassCard({
  id,
  title,
  providerName,
  thumbnailUrl,
  price,
  discountPrice,
  startDate,
  endDate,
  location,
  locationType,
  classType,
  status,
  capacity,
  currentCount,
  closingSoon,
  isPremium,
  rank,
  categoryPath,
  instructors,
  onInstructorClick,
}: ClassCardProps) {
  const statusInfo = status === 'RECRUITING' ? statusLabels.RECRUITING : statusLabels.NOT_RECRUITING;
  const classTypeInfo = classTypeLabels[classType];

  // 할인 여부 및 할인율 계산
  const hasDiscount = price > discountPrice;
  const discountRate = hasDiscount ? Math.round(((price - discountPrice) / price) * 100) : 0;

  return (
    <Link to={`/classes/${id}`}>
      <div className={`rounded-xl shadow-soft hover:shadow-lg transition-shadow group cursor-pointer ${closingSoon ? 'animate-closing-soon' : 'bg-white'}`}>
        {/* Thumbnail */}
        <div className="relative">
          <img
            src={thumbnailUrl}
            alt={title}
            className="w-full block rounded-t-xl object-cover bg-gray-100"
            style={{ aspectRatio: '16/9' }}
          />
          <span className={`absolute top-2 right-2 text-xs font-bold px-2 py-1 rounded ${statusInfo.color}`}>
            {statusInfo.text}
          </span>
          {closingSoon && status === 'RECRUITING' && (
            <span className={`absolute bottom-2 ${rank != null ? 'left-2' : 'right-2'} text-xs font-bold px-2 py-1 rounded-full bg-red-500 text-white shadow-lg flex items-center gap-1`}>
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" />
              <span className="text-xs font-bold">마감 임박</span>
            </span>
          )}
          {rank != null && (
            <span
              className="absolute bottom-1 right-2 z-20 text-4xl font-black leading-none select-none"
              style={{
                color: RANK_COLORS[(rank - 1) % RANK_COLORS.length],
                WebkitTextStroke: '1.5px white',
                textShadow: '0 2px 4px rgba(0,0,0,0.45)',
              }}
            >
              {rank}
            </span>
          )}
          <span className="absolute top-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
            {locationTypeLabels[locationType]}
          </span>
          <span className={`absolute top-10 left-2 text-xs font-bold px-2 py-1 rounded ${classTypeInfo.color}`}>
            {classTypeInfo.text}
          </span>
        </div>

        {/* Content */}
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors line-clamp-2">
            {title}
          </h3>

          {/* 카테고리 태그 (1·2·3차, 레벨별 옅은 파스텔 원형 태그) */}
          {categoryPath && categoryPath.length > 0 && (
            <div className="flex flex-wrap items-center gap-1 mt-1.5">
              {categoryPath.map((cat) => (
                <span
                  key={cat.id}
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium leading-tight ${
                    categoryTagColors[cat.level] || 'bg-gray-50 text-gray-500'
                  }`}
                >
                  {cat.name}
                </span>
              ))}
            </div>
          )}

          <p className="text-sm text-gray-500 mt-1">{providerName}</p>

          {/* Instructor names (premium only) */}
          {isPremium && instructors && instructors.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-1 mt-1 text-xs text-left">
              <span className="text-primary">강사:</span>
              {instructors.map((instructor, idx) => (
                <span key={instructor.id}>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onInstructorClick?.(instructor.id);
                    }}
                    className="text-primary hover:underline"
                  >
                    {instructor.name}
                  </button>
                  {idx < instructors.length - 1 && <span className="text-gray-400">,</span>}
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 mt-3 text-xs text-gray-400">
            <MapPin className="w-3 h-3" />
            <span className="truncate">{location}</span>
          </div>

          <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
            <Calendar className="w-3 h-3" />
            {/* 과거 수업(가격 0)은 시작일 미입력 → "-"로 표시 */}
            <span>{price === 0 ? '-' : startDate} ~ {endDate}</span>
          </div>

          <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
            <Users className="w-3 h-3" />
            <span>{currentCount}/{capacity}명</span>
          </div>

          <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
            <div className="flex flex-col">
              {discountPrice > 0 ? (
                <>
                  {hasDiscount && (
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-gray-400 line-through">
                        ₩{price.toLocaleString()}
                      </span>
                      <span className="text-xs font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded">
                        {discountRate}% 할인
                      </span>
                    </div>
                  )}
                  <span className="font-bold text-primary text-lg">
                    ₩{discountPrice.toLocaleString()}
                  </span>
                </>
              ) : (
                <span className="font-bold text-primary text-lg">-</span>
              )}
            </div>
            <span className="text-xs text-accent font-medium">
              {capacity - currentCount}자리 남음
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
